import React, { useState, useEffect } from 'react';
import {
  Container,
  GridLegacy as Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  IconButton,
  Tooltip,
  SelectChangeEvent,
  Button,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  Speed as SpeedIcon,
  Assessment as AssessmentIcon,
  Groups as GroupsIcon,
  AccessTime as AccessTimeIcon,
  EmojiEvents as TrophyIcon,
  Leaderboard as LeaderboardIcon,
  AttachMoney as AttachMoneyIcon,
  MenuBook as MenuBookIcon,
  Phone as PhoneIcon,
  PhoneInTalk as PhoneInTalkIcon,
  Receipt as ReceiptIcon,
  AdminPanelSettings as AdminIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Timeline as TimelineIcon,
  ReportProblem as EscalationIcon,
  CalendarMonth as CalendarIcon,
  CompareArrows as CompareIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  School as CoachingIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { format, parseISO } from 'date-fns';
import { User, Organization, Module } from '../types';

interface ModularDashboardProps {
  user: User;
  userRole: string;
  selectedOrg: Organization | null;
  hasMultipleOrgs?: boolean;
}

interface ModuleConfig {
  id: string;
  title: string;
  description: string;
  icon: React.ReactElement;
  path: string;
  color: string;
  category: string;
  show: boolean;
}

interface GroupedModules {
  [category: string]: ModuleConfig[];
}

type CommissionPeriod = 'currentWeek' | 'previousWeek' | 'currentMonth' | 'previousMonth';

const ModularDashboard: React.FC<ModularDashboardProps> = ({ 
  user, 
  userRole, 
  selectedOrg, 
  hasMultipleOrgs = false 
}) => {
  const navigate = useNavigate();
  const [commissionPeriod, setCommissionPeriod] = useState<CommissionPeriod>('currentWeek');
  const [showSalesDetails, setShowSalesDetails] = useState(false);
  const [fullnameAttrId, setFullnameAttrId] = useState('');
  const [copySuccess, setCopySuccess] = useState('');

  // Use user's primary attribution ID from DB, fallback to username-derived
  useEffect(() => {
    if (user && user.attribution_id) {
      setFullnameAttrId(user.attribution_id);
      console.log('✅ Attribution ID set to:', user.attribution_id);
    } else if (user && user.username) {
      const fullnameId = user.username.replace(/\s+/g, '');
      setFullnameAttrId(fullnameId);
      console.log('✅ Attribution ID fallback set to:', fullnameId);
    }
  }, [user]);

  // Copy link to clipboard
  const copyToClipboard = (link: string) => {
    navigator.clipboard.writeText(link).then(() => {
      setCopySuccess(link);
      setTimeout(() => setCopySuccess(''), 2000);
    });
  };

  // Check permissions
  const isDirectMeds = selectedOrg && selectedOrg.name === 'Direct Meds';
  const isManagerOrSuperuser = !!(userRole === 'manager' || userRole === 'superuser' || user?.is_master);
  const isCustomer = user?.permission_group_id === 4; // Permission Group ID 4 = Customer

  // Icon mapping helper function
  const getIconComponent = (iconName: string): React.ReactElement => {
    const iconMap: Record<string, React.ReactElement> = {
      'AttachMoney': <AttachMoneyIcon sx={{ fontSize: 48 }} />,
      'Assessment': <AssessmentIcon sx={{ fontSize: 48 }} />,
      'Groups': <GroupsIcon sx={{ fontSize: 48 }} />,
      'AdminPanelSettings': <AdminIcon sx={{ fontSize: 48 }} />,
      'Speed': <SpeedIcon sx={{ fontSize: 48 }} />,
      'Analytics': <AnalyticsIcon sx={{ fontSize: 48 }} />,
      'Timeline': <TimelineIcon sx={{ fontSize: 48 }} />,
      'CompareArrows': <CompareIcon sx={{ fontSize: 48 }} />,
      'Phone': <PhoneIcon sx={{ fontSize: 48 }} />,
      'PhoneInTalk': <PhoneInTalkIcon sx={{ fontSize: 48 }} />,
      'School': <CoachingIcon sx={{ fontSize: 48 }} />,
      'Leaderboard': <LeaderboardIcon sx={{ fontSize: 48 }} />,
      'CalendarMonth': <CalendarIcon sx={{ fontSize: 48 }} />,
      'EmojiEvents': <TrophyIcon sx={{ fontSize: 48 }} />,
      'Receipt': <ReceiptIcon sx={{ fontSize: 48 }} />,
      'MenuBook': <MenuBookIcon sx={{ fontSize: 48 }} />,
      'ReportProblem': <EscalationIcon sx={{ fontSize: 48 }} />,
      'AccessTime': <AccessTimeIcon sx={{ fontSize: 48 }} />,
    };
    
    return iconMap[iconName] || <DashboardIcon sx={{ fontSize: 48 }} />;
  };

  // Fetch user's allowed modules from Permission Groups system
  const {
    data: userModules,
    isLoading: modulesLoading,
    error: modulesError,
  } = useQuery({
    queryKey: ['user-modules', user?.id, user?.permission_group_id, selectedOrg?.id],
    queryFn: () => api.getUserAllowedModules(),
    enabled: !!user && !!user.id,
    retry: 2,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Partner User detection: identified by having partner-specific modules in their allowed list
  const isPartnerUser = !!(userModules && (userModules as Module[]).some(
    (m: Module) => m.module_key === 'partner-efficiency' || m.module_key === 'partner-analytics'
  ));

  // Weekly planning only applies when the org/user is actually entitled to it
  const hasWeeklyPlanningModule = !!(userModules && (userModules as Module[]).some(
    (m: Module) => m.module_key === 'weekly-planning' || m.module_key === 'user-weekly-planning'
  ));

  // Embed Sales MTD revenue tile only applies to users entitled to the Embed Sales module (1776 Rx)
  const hasEmbedSalesModule = !!(userModules && (userModules as Module[]).some(
    (m: Module) => m.module_key === 'embed-sales'
  ));

  // Transform API modules to ModuleConfig format while preserving organization & W2 logic
  const allModulesFromAPI: ModuleConfig[] = (userModules || []).map((module: Module) => ({
    id: module.module_key,
    title: module.title,
    description: module.description || '',
    icon: getIconComponent(module.icon_name),
    path: module.route_path,
    color: module.color,
    category: module.category?.name || 'Other',
    show: true, // Already filtered by backend based on permission groups
  }));

  // Apply organization-based filtering on top of permission groups
  const filteredModules = allModulesFromAPI.filter(module => {
    // Direct Meds specific modules - only show if user is in Direct Meds org
    if (['commission-calculator', 'sales-audit', 'team-comparison', 'enhanced-efficiency',
         'call-quality', 'previous-week-coaching', 'agent-efficiency', 'team-scorecard', 
         'weekly-planning'].includes(module.id)) {
      return isDirectMeds;
    }
    // All other modules pass through
    return true;
  });

  // No additional filtering needed - backend handles permissions via permission groups
  const allModules = filteredModules;

  // Filter modules based on show property
  const availableModules = allModules.filter(module => module.show);

  // Group modules by category
  const groupedModules: GroupedModules = availableModules.reduce((acc, module) => {
    if (!acc[module.category]) {
      acc[module.category] = [];
    }
    acc[module.category].push(module);
    return acc;
  }, {} as GroupedModules);

  const handleModuleClick = (path: string) => {
    navigate(path);
  };

  const handlePeriodChange = (event: SelectChangeEvent<CommissionPeriod>) => {
    setCommissionPeriod(event.target.value as CommissionPeriod);
  };

  // Fetch commission data for Direct Meds regular users (not superusers, managers, or customers)
  const {
    data: commissionData,
    isLoading: commissionLoading,
    error: commissionError,
  } = useQuery({
    queryKey: ['personalCommission', commissionPeriod, user?.id],
    queryFn: () => api.getPersonalCommission(commissionPeriod, user?.id),
    refetchInterval: 5 * 60 * 1000,
    enabled: !!user && !!user.id && !!isDirectMeds && !isManagerOrSuperuser && !isCustomer,
    retry: 1,
  });

  // Company-wide month-to-date Embed Sales revenue (1776 Rx). Only fetched
  // when the user's visible modules include Embed Sales.
  const {
    data: mtdRevenue,
    isLoading: mtdRevenueLoading,
    isError: mtdRevenueError,
  } = useQuery({
    queryKey: ['embed-sales-mtd-summary'],
    queryFn: () => api.getEmbedSalesMtdSummary(),
    enabled: hasEmbedSalesModule,
    retry: 1,
  });

  // Fetch team metrics for Direct Meds (managers/superusers/customers)
  const {
    data: teamMetrics,
    isLoading: teamMetricsLoading,
  } = useQuery({
    queryKey: ['teamSalesMetrics'],
    queryFn: () => api.getSalesMetrics(),
    refetchInterval: 2 * 60 * 1000,
    enabled: !!user && !!isDirectMeds && (isManagerOrSuperuser || isCustomer),
    retry: 1,
  });

  // Fetch team efficiency metrics (SPH & Win Rate) for Direct Meds (managers/superusers/customers)
  const {
    data: teamEfficiencyMetrics,
    isLoading: efficiencyLoading,
  } = useQuery({
    queryKey: ['teamEfficiencyMetrics'],
    queryFn: () => api.getTeamEfficiencyMetrics(),
    refetchInterval: 2 * 60 * 1000,
    enabled: !!user && !!isDirectMeds && (isManagerOrSuperuser || isCustomer),
    retry: 1,
  });

  // Check if user has submitted weekly planning (for regular users, not superusers or managers)
  const {
    data: weeklyPlanStatus,
  } = useQuery({
    queryKey: ['weeklyPlanStatus', user?.id],
    queryFn: () => api.getCurrentWeeklyPlan(),
    refetchInterval: 5 * 60 * 1000,
    enabled: !!user && !!user.id && userRole !== 'superuser' && userRole !== 'manager' && hasWeeklyPlanningModule,
    retry: 1,
  });

  // Check if user needs to submit weekly planning
  const needsWeeklyPlanning = weeklyPlanStatus && (
    !weeklyPlanStatus.plan || 
    (weeklyPlanStatus.plan.total_planned_hours === 0 || weeklyPlanStatus.plan.total_planned_hours === null)
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Simple Separator */}
      <Box sx={{ borderBottom: '1px solid #000', mb: 3 }} />

      {/* Organization Header */}
      {selectedOrg && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            bgcolor: '#fafafa',
            borderLeft: '4px solid #4B88A2',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <BusinessIcon sx={{ fontSize: 40, color: '#4B88A2' }} />
          <Box>
            <Typography variant="h6" fontWeight="600" sx={{ color: '#2C3E50' }}>
              Welcome, {user?.username}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedOrg.name}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Month to Date Revenue - company-wide Embed Sales totals (1776 Rx) */}
      {hasEmbedSalesModule && (
        <Card elevation={3} sx={{ mb: 3, backgroundColor: '#f0f7f4', border: '2px solid #2e7d32' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <AttachMoneyIcon sx={{ color: '#2e7d32', fontSize: 28 }} />
                <Typography variant="h5" fontWeight="bold" sx={{ color: '#0C0C0C' }}>
                  Month to Date Revenue
                </Typography>
              </Box>
              {mtdRevenue?.monthLabel && (
                <Chip
                  label={mtdRevenue.monthLabel}
                  sx={{ backgroundColor: '#2e7d32', color: 'white', fontWeight: 'bold' }}
                />
              )}
            </Box>
            {mtdRevenueLoading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={32} sx={{ color: '#2e7d32' }} />
              </Box>
            ) : mtdRevenueError ? (
              <Alert severity="error">Unable to load month to date revenue totals.</Alert>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ backgroundColor: 'rgba(46, 125, 50, 0.15)', border: '2px solid #2e7d32', height: '100%' }}>
                    <CardContent>
                      <Typography variant="caption" fontWeight="bold" color="success.main" gutterBottom display="block">
                        TOTAL REVENUE
                      </Typography>
                      <Typography variant="h3" fontWeight="bold" color="success.main">
                        ${Number(mtdRevenue?.totalRevenue ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ backgroundColor: 'rgba(255, 152, 0, 0.15)', border: '2px solid #ff9800', height: '100%' }}>
                    <CardContent>
                      <Typography variant="caption" fontWeight="bold" color="warning.main" gutterBottom display="block">
                        NUMBER OF SALES
                      </Typography>
                      <Typography variant="h3" fontWeight="bold" color="warning.main">
                        {Number(mtdRevenue?.salesCount ?? 0).toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ backgroundColor: 'rgba(156, 39, 176, 0.12)', border: '2px solid #9c27b0', height: '100%' }}>
                    <CardContent>
                      <Typography variant="caption" fontWeight="bold" sx={{ color: '#9c27b0' }} gutterBottom display="block">
                        TOP PRODUCT
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" sx={{ color: '#9c27b0' }}>
                        {mtdRevenue?.topProduct ? mtdRevenue.topProduct.name : '—'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {mtdRevenue?.topProduct
                          ? `${mtdRevenue.topProduct.count} sales · $${Number(mtdRevenue.topProduct.revenue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : 'No sales logged this month'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      )}


      {/* Weekly Planning Reminder - Only for orgs entitled to weekly planning */}
      {hasWeeklyPlanningModule && userRole !== 'superuser' && userRole !== 'manager' && !isPartnerUser && weeklyPlanStatus && (
        <>
          {needsWeeklyPlanning ? (
            <Alert 
              severity="warning" 
              sx={{ 
                mb: 3,
                border: '2px solid #ff9800',
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                '& .MuiAlert-icon': {
                  fontSize: 32
                }
              }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  variant="contained"
                  sx={{ 
                    bgcolor: '#ff9800', 
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#f57c00' }
                  }}
                  onClick={() => navigate('/weekly-planning')}
                  startIcon={<CalendarIcon />}
                >
                  Update Now
                </Button>
              }
            >
              <Typography variant="body1" fontWeight="600">
                ⚠️ Weekly Planning Hours Not Submitted
              </Typography>
              <Typography variant="body2">
                Please update your weekly planning hours immediately. This helps management plan team capacity.
              </Typography>
            </Alert>
          ) : (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3,
                border: '2px solid #4caf50',
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                '& .MuiAlert-icon': {
                  fontSize: 32
                }
              }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  sx={{ 
                    borderColor: '#4caf50',
                    color: '#4caf50',
                    fontWeight: 600,
                    '&:hover': { 
                      borderColor: '#45a049',
                      bgcolor: 'rgba(76, 175, 80, 0.1)'
                    }
                  }}
                  onClick={() => navigate('/weekly-planning')}
                  startIcon={<CalendarIcon />}
                >
                  Update Hours
                </Button>
              }
            >
              <Typography variant="body1" fontWeight="600">
                ✅ Weekly Planning Hours Committed
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                You have committed to <strong>{weeklyPlanStatus.plan?.total_planned_hours || 0} hours</strong> this week:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {weeklyPlanStatus.plan?.sunday_hours > 0 && (
                  <Chip 
                    size="small" 
                    label={`Sun: ${weeklyPlanStatus.plan.sunday_hours}h`} 
                    sx={{ bgcolor: '#9c27b020', color: '#9c27b0', fontWeight: 600 }} 
                  />
                )}
                {weeklyPlanStatus.plan?.monday_hours > 0 && (
                  <Chip 
                    size="small" 
                    label={`Mon: ${weeklyPlanStatus.plan.monday_hours}h`} 
                    sx={{ bgcolor: '#2196F320', color: '#2196F3', fontWeight: 600 }} 
                  />
                )}
                {weeklyPlanStatus.plan?.tuesday_hours > 0 && (
                  <Chip 
                    size="small" 
                    label={`Tue: ${weeklyPlanStatus.plan.tuesday_hours}h`} 
                    sx={{ bgcolor: '#00bcd420', color: '#00bcd4', fontWeight: 600 }} 
                  />
                )}
                {weeklyPlanStatus.plan?.wednesday_hours > 0 && (
                  <Chip 
                    size="small" 
                    label={`Wed: ${weeklyPlanStatus.plan.wednesday_hours}h`} 
                    sx={{ bgcolor: '#00968820', color: '#009688', fontWeight: 600 }} 
                  />
                )}
                {weeklyPlanStatus.plan?.thursday_hours > 0 && (
                  <Chip 
                    size="small" 
                    label={`Thu: ${weeklyPlanStatus.plan.thursday_hours}h`} 
                    sx={{ bgcolor: '#4caf5020', color: '#4caf50', fontWeight: 600 }} 
                  />
                )}
                {weeklyPlanStatus.plan?.friday_hours > 0 && (
                  <Chip 
                    size="small" 
                    label={`Fri: ${weeklyPlanStatus.plan.friday_hours}h`} 
                    sx={{ bgcolor: '#ff980020', color: '#ff9800', fontWeight: 600 }} 
                  />
                )}
                {weeklyPlanStatus.plan?.saturday_hours > 0 && (
                  <Chip 
                    size="small" 
                    label={`Sat: ${weeklyPlanStatus.plan.saturday_hours}h`} 
                    sx={{ bgcolor: '#ff572220', color: '#ff5722', fontWeight: 600 }} 
                  />
                )}
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666' }}>
                Please update if there are any changes.
              </Typography>
            </Alert>
          )}
        </>
      )}

      {/* Affiliate Links Section - Direct Meds (Soniq CX) organization only */}
      {fullnameAttrId && isDirectMeds && (
        <Paper 
          sx={{ 
            p: 3, 
            mb: 4, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            boxShadow: 3
          }}
        >
          <Box display="flex" alignItems="center" mb={2}>
            <LinkIcon sx={{ mr: 1, fontSize: 32 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              My Affiliate Links
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {/* Link 1: DirectMax */}
            {[
              { label: 'DirectMax', url: `https://directmeds.com/dm-directmax-ts/index-rm.php?ct=3&oid=4&uid=122&affid=15&source_id=${fullnameAttrId}&sub1=scx` },
              { label: 'GLP-1 Telesales Lifetime Discount #1', url: `https://directmeds.com/dm-offers-sp-ts/?oid=4&uid=81&affid=15&ct=3&source_id=${fullnameAttrId}&sub1=drm` },
              { label: 'HRT', url: `https://directmeds.com/dm-nuvella-ts/rm-index.php?ct=3&oid=4&uid=123&affid=15&source_id=${fullnameAttrId}&sub1=scx` },
              { label: 'NAD+', url: `https://directmeds.com/dm-nad-sp-ts/?oid=4&uid=82&affid=15&ct=3&source_id=${fullnameAttrId}&sub1=scx` },
            ].map(({ label, url }) => (
              <Grid item xs={12} key={label}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.95)' }}>
                  <Typography variant="subtitle2" color="secondary" sx={{ fontWeight: 600, mb: 1 }}>
                    {label}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <TextField
                      fullWidth
                      size="small"
                      value={url}
                      InputProps={{
                        readOnly: true,
                        sx: { fontSize: '0.875rem', fontFamily: 'monospace' }
                      }}
                    />
                    <Tooltip title={copySuccess === url ? 'Copied!' : 'Copy link'}>
                      <IconButton
                        onClick={() => copyToClipboard(url)}
                        sx={{
                          bgcolor: copySuccess === url ? '#4caf50' : '#dc004e',
                          color: 'white',
                          '&:hover': {
                            bgcolor: copySuccess === url ? '#45a049' : '#c70039'
                          }
                        }}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Team Sales Overview - For Managers/Superusers/Customers */}
      {isDirectMeds && (isManagerOrSuperuser || isCustomer) && (
        <Paper sx={{ p: 3, mb: 4, border: '2px solid #2196F3', boxShadow: 2, backgroundColor: '#F8F9FA' }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <DashboardIcon sx={{ fontSize: 40, color: '#2196F3' }} />
            <Box>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#0C0C0C' }}>
                Team Sales Overview
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Real-time team performance
              </Typography>
            </Box>
          </Box>

          {teamMetricsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : teamMetrics?.metrics ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: 'rgba(33, 150, 243, 0.1)', border: '2px solid #2196F3', height: '100%' }}>
                  <CardContent>
                    <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                      Today's Sales
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="primary">
                      {teamMetrics.metrics.todaySales || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: 'rgba(76, 175, 80, 0.1)', border: '2px solid #4caf50', height: '100%' }}>
                  <CardContent>
                    <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                      This Week
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="success.main">
                      {teamMetrics.metrics.weekSales || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: 'rgba(255, 152, 0, 0.1)', border: '2px solid #ff9800', height: '100%' }}>
                  <CardContent>
                    <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                      Last Week (Same Time)
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: '#ff9800' }}>
                      {teamMetrics.metrics.lastWeekSales || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  backgroundColor: (teamMetrics.metrics.weekSales || 0) >= (teamMetrics.metrics.lastWeekSales || 0) ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)', 
                  border: `2px solid ${(teamMetrics.metrics.weekSales || 0) >= (teamMetrics.metrics.lastWeekSales || 0) ? '#4caf50' : '#f44336'}`,
                  height: '100%' 
                }}>
                  <CardContent>
                    <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                      Week Comparison
                    </Typography>
                    <Typography 
                      variant="h3" 
                      fontWeight="bold" 
                      sx={{ color: (teamMetrics.metrics.weekSales || 0) >= (teamMetrics.metrics.lastWeekSales || 0) ? '#4caf50' : '#f44336' }}
                    >
                      {((teamMetrics.metrics.weekSales || 0) - (teamMetrics.metrics.lastWeekSales || 0)) >= 0 ? '+' : ''}
                      {((teamMetrics.metrics.weekSales || 0) - (teamMetrics.metrics.lastWeekSales || 0))}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      {(teamMetrics.metrics.weekSales || 0) >= (teamMetrics.metrics.lastWeekSales || 0) ? '📈 Ahead' : '📉 Behind'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Sales Per Hour (SPH) - This Week */}
              <Grid item xs={12} sm={6} md={2.4}>
                <Card sx={{ backgroundColor: 'rgba(156, 39, 176, 0.1)', border: '2px solid #9c27b0', height: '100%' }}>
                  <CardContent>
                    <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                      Sales Per Hour (This Week)
                    </Typography>
                    {efficiencyLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <Typography variant="h3" fontWeight="bold" sx={{ color: '#9c27b0' }}>
                        {teamEfficiencyMetrics?.metrics?.thisWeek?.avgSPH || '0'}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* SPH Comparison */}
              <Grid item xs={12} sm={6} md={2.4}>
                <Card sx={{ 
                  backgroundColor: parseFloat(teamEfficiencyMetrics?.metrics?.comparison?.sphDiff || '0') >= 0 ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)', 
                  border: `2px solid ${parseFloat(teamEfficiencyMetrics?.metrics?.comparison?.sphDiff || '0') >= 0 ? '#4caf50' : '#f44336'}`,
                  height: '100%' 
                }}>
                  <CardContent>
                    <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                      SPH vs Last Week
                    </Typography>
                    {efficiencyLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <>
                        <Typography 
                          variant="h3" 
                          fontWeight="bold" 
                          sx={{ color: parseFloat(teamEfficiencyMetrics?.metrics?.comparison?.sphDiff || '0') >= 0 ? '#4caf50' : '#f44336' }}
                        >
                          {parseFloat(teamEfficiencyMetrics?.metrics?.comparison?.sphDiff || '0') >= 0 ? '+' : ''}
                          {teamEfficiencyMetrics?.metrics?.comparison?.sphDiff || '0'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Last week: {teamEfficiencyMetrics?.metrics?.lastWeekSameTime?.avgSPH || '0'}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Close % (Win Rate) - This Week */}
              <Grid item xs={12} sm={6} md={2.4}>
                <Card sx={{ backgroundColor: 'rgba(0, 150, 136, 0.1)', border: '2px solid #009688', height: '100%' }}>
                  <CardContent>
                    <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                      Close % (This Week)
                    </Typography>
                    {efficiencyLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <Typography variant="h3" fontWeight="bold" sx={{ color: '#009688' }}>
                        {teamEfficiencyMetrics?.metrics?.thisWeek?.avgWinRate || '0'}%
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Close % Comparison */}
              <Grid item xs={12} sm={6} md={2.4}>
                <Card sx={{ 
                  backgroundColor: parseFloat(teamEfficiencyMetrics?.metrics?.comparison?.winRateDiff || '0') >= 0 ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)', 
                  border: `2px solid ${parseFloat(teamEfficiencyMetrics?.metrics?.comparison?.winRateDiff || '0') >= 0 ? '#4caf50' : '#f44336'}`,
                  height: '100%' 
                }}>
                  <CardContent>
                    <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                      Close % vs Last Week
                    </Typography>
                    {efficiencyLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <>
                        <Typography 
                          variant="h3" 
                          fontWeight="bold" 
                          sx={{ color: parseFloat(teamEfficiencyMetrics?.metrics?.comparison?.winRateDiff || '0') >= 0 ? '#4caf50' : '#f44336' }}
                        >
                          {parseFloat(teamEfficiencyMetrics?.metrics?.comparison?.winRateDiff || '0') >= 0 ? '+' : ''}
                          {teamEfficiencyMetrics?.metrics?.comparison?.winRateDiff || '0'}%
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Last week: {teamEfficiencyMetrics?.metrics?.lastWeekSameTime?.avgWinRate || '0'}%
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info">
              No team metrics available at this time.
            </Alert>
          )}
        </Paper>
      )}

      {/* Commission Tracker - Only for Direct Meds regular users (not superusers, managers, customers, or partner users) */}
      {isDirectMeds && !isManagerOrSuperuser && !isCustomer && !isPartnerUser && (
        <Paper sx={{ p: 3, mb: 4, border: '2px solid #61ABBC', boxShadow: 2, backgroundColor: '#F8F9FA' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <AttachMoneyIcon sx={{ fontSize: 40, color: '#4B88A2' }} />
              <Box>
                <Typography variant="h5" fontWeight="bold" sx={{ color: '#0C0C0C' }}>
                  Commission Tracker
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {commissionData?.weekStart && commissionData?.weekEnd && (
                    <>Week of {format(parseISO(commissionData.weekStart), 'MMM dd')} - {format(parseISO(commissionData.weekEnd), 'MMM dd, yyyy')}</>
                  )}
                </Typography>
              </Box>
            </Box>
            <FormControl size="small" sx={{ minWidth: 180, borderRadius: 1, backgroundColor: 'white' }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={commissionPeriod}
                label="Period"
                onChange={handlePeriodChange}
              >
                <MenuItem value="currentWeek">Current Week</MenuItem>
                <MenuItem value="previousWeek">Previous Week</MenuItem>
                <MenuItem value="currentMonth">Current Month</MenuItem>
                <MenuItem value="previousMonth">Previous Month</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {commissionLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress sx={{ color: 'white' }} />
            </Box>
          ) : commissionError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Unable to load commission data. This may be because you don't have sales data recorded yet.
            </Alert>
          ) : commissionData?.commission ? (
            <>
              {/* Different display for Cali Crushers (flat $50, no kickers), W2, and 1099 employees */}
              {(user?.team === 'Cali Crushers' && !(user?.username || '').toLowerCase().includes('joseph')) ? (
                // Cali Crushers View: Flat $50 per sale, no weekend/over-20 kickers
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ backgroundColor: 'rgba(255,255,255,0.95)', height: '100%' }}>
                      <CardContent>
                        <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                          Total Sales
                        </Typography>
                        <Typography variant="h3" fontWeight="bold" color="primary">
                          {commissionData.commission.totalSales}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ backgroundColor: 'rgba(76, 175, 80, 0.15)', border: '2px solid #4caf50', height: '100%' }}>
                      <CardContent>
                        <Typography variant="caption" fontWeight="bold" color="success.main" gutterBottom display="block">
                          TOTAL COMMISSION
                        </Typography>
                        <Typography variant="h3" fontWeight="bold" color="success.main">
                          ${commissionData.commission.totalCommission.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666', mt: 1, display: 'block' }}>
                          $50 per sale × {commissionData.commission.totalSales} sales
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              ) : user?.employment_type === 'W2' ? (
                // W2 Employee View: $10/sale starting from 11th sale
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ backgroundColor: 'rgba(255,255,255,0.95)', height: '100%' }}>
                      <CardContent>
                        <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                          Total Sales This Week
                        </Typography>
                        <Typography variant="h3" fontWeight="bold" color="primary">
                          {commissionData.commission.totalSales}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {commissionData.commission.totalSales <= 10 ? (
                    // Before hitting 11 sales - show how many more needed
                    <Grid item xs={12} sm={6} md={4}>
                      <Card sx={{ backgroundColor: 'rgba(255, 152, 0, 0.15)', border: '2px solid #ff9800', height: '100%' }}>
                        <CardContent>
                          <Typography variant="caption" fontWeight="bold" color="warning.main" gutterBottom display="block">
                            SALES TO START EARNING
                          </Typography>
                          <Typography variant="h3" fontWeight="bold" color="warning.main">
                            {11 - commissionData.commission.totalSales} more
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#666', mt: 1, display: 'block' }}>
                            Commission starts on 11th sale
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ) : (
                    // After hitting 11 sales - show commission earned
                    <Grid item xs={12} sm={6} md={4}>
                      <Card sx={{ backgroundColor: 'rgba(76, 175, 80, 0.15)', border: '2px solid #4caf50', height: '100%' }}>
                        <CardContent>
                          <Typography variant="caption" fontWeight="bold" color="success.main" gutterBottom display="block">
                            TOTAL COMMISSION EARNED
                          </Typography>
                          <Typography variant="h3" fontWeight="bold" color="success.main">
                            ${commissionData.commission.totalCommission.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#666', mt: 1, display: 'block' }}>
                            $10 per sale × {commissionData.commission.totalSales - 10} commissionable sales
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              ) : (
                // 1099 Contractor View: Original commission structure
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card sx={{ backgroundColor: 'rgba(255,255,255,0.95)', height: '100%' }}>
                      <CardContent>
                        <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                          Total Sales
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" color="primary">
                          {commissionData.commission.totalSales}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card sx={{ backgroundColor: 'rgba(255,255,255,0.95)', height: '100%' }}>
                      <CardContent>
                        <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                          Weekday Sales
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" color="info.main">
                          {commissionData.commission.weekdaySales}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card sx={{ backgroundColor: 'rgba(255,255,255,0.95)', height: '100%' }}>
                      <CardContent>
                        <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                          Weekend Sales
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" color="secondary.main">
                          {commissionData.commission.weekendSales}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card sx={{ backgroundColor: 'rgba(255,255,255,0.95)', height: '100%' }}>
                      <CardContent>
                        <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                          Sales Over 20
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" color="success.main">
                          {commissionData.commission.salesOver20}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card sx={{ backgroundColor: 'rgba(76, 175, 80, 0.15)', border: '2px solid #4caf50', height: '100%' }}>
                      <CardContent>
                        <Typography variant="caption" fontWeight="bold" color="success.main" gutterBottom display="block">
                          TOTAL COMMISSION
                        </Typography>
                        <Typography variant="h3" fontWeight="bold" color="success.main">
                          ${commissionData.commission.totalCommission.toLocaleString()}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Commission breakdown info */}
              <Box mt={2} p={2} sx={{ backgroundColor: 'white', borderRadius: 1, border: '1px solid #61ABBC' }}>
                <Typography variant="body2" sx={{ color: '#0C0C0C' }} fontWeight={600} gutterBottom>
                  📊 Commission Structure:
                </Typography>
                {(() => {
                  // Check if this is Mike Hales (special $75 flat rate)
                  const isMikeHales = (
                    (user?.username && (
                      user.username.toLowerCase().includes('mike hales') ||
                      user.username.toLowerCase().includes('michael hales')
                    )) ||
                    (user?.sales_tracker_id && user.sales_tracker_id.toLowerCase() === 'mike h')
                  );
                  
                  // Check if this is Matt James or Joseph Manukyan (special $65 flat rate)
                  const isFlatRate65Agent = (
                    (user?.username && (
                      user.username.toLowerCase().includes('matt james') ||
                      user.username.toLowerCase().includes('joseph manukyan')
                    )) ||
                    (user?.sales_tracker_id && (
                      user.sales_tracker_id.toLowerCase() === 'josephm' ||
                      user.sales_tracker_id.toLowerCase() === 'mattj'
                    ))
                  );
                  
                  // Check if user is a manager (also $75 flat rate)
                  const isManager = userRole === 'manager';
                  
                  // Cali Crushers team → flat $50 per sale (Joseph keeps his existing $65 rate)
                  const isCaliCrusher = user?.team === 'Cali Crushers' && !isFlatRate65Agent;
                  
                  if (isCaliCrusher) {
                    return (
                      <Box display="flex" flexWrap="wrap" gap={2}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          • Flat Rate: $50 per sale
                        </Typography>
                      </Box>
                    );
                  } else if (isMikeHales) {
                    return (
                      <Box display="flex" flexWrap="wrap" gap={2}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          • Flat Rate: $75 per sale
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          • No minimum, no bonuses
                        </Typography>
                      </Box>
                    );
                  } else if (isFlatRate65Agent) {
                    return (
                      <Box display="flex" flexWrap="wrap" gap={2}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          • Flat Rate: $65 per sale
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          • No minimum, no bonuses
                        </Typography>
                      </Box>
                    );
                  } else if (isManager) {
                    return (
                      <Box display="flex" flexWrap="wrap" gap={2}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          • Manager Rate: $75 per sale
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          • No minimum, no bonuses
                        </Typography>
                      </Box>
                    );
                  } else if (user?.employment_type === 'W2') {
                    return (
                      <Box display="flex" flexWrap="wrap" gap={2}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          • First 10 sales: $0 commission
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          • Sales 11+: $10 per sale
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          • Commission starts on your 11th sale
                        </Typography>
                      </Box>
                    );
                  } else {
                    return (
                      <Box display="flex" flexWrap="wrap" gap={2}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          • Sales 1-20: $50 each
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          • Sales 21+: $65 each
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          • Weekend Bonus: +$10 per sale
                        </Typography>
                      </Box>
                    );
                  }
                })()}
                {(commissionData.commission.nonValidatedCount > 0 || commissionData.commission.attributionOnlyCount > 0 || commissionData.commission.cancelledCount > 0) && (
                  <Box mt={1}>
                    <Typography variant="caption" color="warning.main" fontWeight={600}>
                      ⚠️ Excluded from commission: {commissionData.commission.nonValidatedCount} non-validated, {commissionData.commission.attributionOnlyCount} attribution-only, {commissionData.commission.cancelledCount} cancelled
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Expandable Sales Details */}
              <Box 
                mt={2} 
                p={2} 
                sx={{ 
                  backgroundColor: 'white', 
                  borderRadius: 1,
                  cursor: 'pointer',
                  border: '2px solid #4B88A2',
                  '&:hover': { backgroundColor: '#e8f4f6', borderColor: '#61ABBC' }
                }}
                onClick={() => setShowSalesDetails(!showSalesDetails)}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ color: '#4B88A2' }} fontWeight={600}>
                    📋 View Sales Details
                  </Typography>
                  {showSalesDetails ? <KeyboardArrowUp sx={{ color: '#4B88A2' }} /> : <KeyboardArrowDown sx={{ color: '#4B88A2' }} />}
                </Box>
              </Box>

              {showSalesDetails && (
                <Box mt={2}>
                  <Grid container spacing={2}>
                    {/* Validated Sales */}
                    {commissionData.commission.validatedSales && commissionData.commission.validatedSales.length > 0 && (
                      <Grid item xs={12} md={6} lg={3}>
                        <Paper sx={{ p: 2, backgroundColor: 'rgba(76, 175, 80, 0.08)', height: '100%' }}>
                          <Box display="flex" alignItems="center" mb={1}>
                            <CheckCircle color="success" sx={{ mr: 1, fontSize: 20 }} />
                            <Typography variant="subtitle2" fontWeight={600}>
                              Validated Sales ({commissionData.commission.validatedSales.length})
                            </Typography>
                          </Box>
                          <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                            {commissionData.commission.validatedSales.map((sale: any, idx: number) => (
                              <Box 
                                key={idx} 
                                sx={{ 
                                  p: 1, 
                                  mb: 0.5, 
                                  backgroundColor: 'white',
                                  borderRadius: 1,
                                  fontSize: '0.85rem'
                                }}
                              >
                                <Typography variant="caption" display="block">
                                  <strong>{format(parseISO(sale.date), 'MMM dd, yyyy')}</strong>
                                  {sale.isWeekend && <Chip label="Weekend" size="small" color="info" sx={{ ml: 1, height: 16, fontSize: '0.7rem' }} />}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-all' }}>
                                  {sale.email}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Paper>
                      </Grid>
                    )}

                    {/* Non-Validated Sales */}
                    {commissionData.commission.nonValidatedSales && commissionData.commission.nonValidatedSales.length > 0 && (
                      <Grid item xs={12} md={6} lg={3}>
                        <Paper sx={{ p: 2, backgroundColor: 'rgba(255, 152, 0, 0.08)', height: '100%' }}>
                          <Box display="flex" alignItems="center" mb={1}>
                            <Warning color="warning" sx={{ mr: 1, fontSize: 20 }} />
                            <Typography variant="subtitle2" fontWeight={600}>
                              Non-Validated ({commissionData.commission.nonValidatedSales.length})
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            In sales tracker but not attribution
                          </Typography>
                          <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                            {commissionData.commission.nonValidatedSales.map((sale: any, idx: number) => (
                              <Box 
                                key={idx} 
                                sx={{ 
                                  p: 1, 
                                  mb: 0.5, 
                                  backgroundColor: 'white',
                                  borderRadius: 1,
                                  fontSize: '0.85rem'
                                }}
                              >
                                <Typography variant="caption" display="block">
                                  <strong>{format(parseISO(sale.date), 'MMM dd, yyyy')}</strong>
                                  {sale.isWeekend && <Chip label="Weekend" size="small" color="info" sx={{ ml: 1, height: 16, fontSize: '0.7rem' }} />}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-all' }}>
                                  {sale.email}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Paper>
                      </Grid>
                    )}

                    {/* Attribution-Only Sales */}
                    {commissionData.commission.attributionOnlySales && commissionData.commission.attributionOnlySales.length > 0 && (
                      <Grid item xs={12} md={6} lg={3}>
                        <Paper sx={{ p: 2, backgroundColor: 'rgba(244, 67, 54, 0.08)', height: '100%' }}>
                          <Box display="flex" alignItems="center" mb={1}>
                            <ErrorIcon color="error" sx={{ mr: 1, fontSize: 20 }} />
                            <Typography variant="subtitle2" fontWeight={600}>
                              Attribution-Only ({commissionData.commission.attributionOnlySales.length})
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            In attribution but not sales tracker
                          </Typography>
                          <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                            {commissionData.commission.attributionOnlySales.map((sale: any, idx: number) => (
                              <Box 
                                key={idx} 
                                sx={{ 
                                  p: 1, 
                                  mb: 0.5, 
                                  backgroundColor: 'white',
                                  borderRadius: 1,
                                  fontSize: '0.85rem'
                                }}
                              >
                                <Typography variant="caption" display="block">
                                  <strong>{format(parseISO(sale.date), 'MMM dd, yyyy')}</strong>
                                  {sale.isWeekend && <Chip label="Weekend" size="small" color="info" sx={{ ml: 1, height: 16, fontSize: '0.7rem' }} />}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-all' }}>
                                  {sale.email}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Paper>
                      </Grid>
                    )}

                    {/* Cancelled Sales */}
                    {commissionData.commission.cancelledSales && commissionData.commission.cancelledSales.length > 0 && (
                      <Grid item xs={12} md={6} lg={3}>
                        <Paper sx={{ p: 2, backgroundColor: 'rgba(211, 47, 47, 0.12)', border: '2px solid rgba(211, 47, 47, 0.5)', height: '100%' }}>
                          <Box display="flex" alignItems="center" mb={1}>
                            <ErrorIcon sx={{ mr: 1, color: '#d32f2f', fontSize: 20 }} />
                            <Typography variant="subtitle2" fontWeight={600} color="#d32f2f">
                              Cancelled ({commissionData.commission.cancelledSales.length})
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ mb: 1, display: 'block', color: '#d32f2f', fontWeight: 600 }}>
                            ⚠️ EXCLUDED from commission
                          </Typography>
                          <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                            {commissionData.commission.cancelledSales.map((sale: any, idx: number) => (
                              <Box 
                                key={idx} 
                                sx={{ 
                                  p: 1, 
                                  mb: 0.5, 
                                  backgroundColor: 'white',
                                  borderRadius: 1,
                                  fontSize: '0.85rem',
                                  opacity: 0.7
                                }}
                              >
                                <Typography variant="caption" display="block">
                                  <strong>{format(parseISO(sale.date), 'MMM dd, yyyy')}</strong>
                                  <Chip label="CANCELLED" size="small" sx={{ ml: 1, height: 16, backgroundColor: '#d32f2f', color: 'white', fontSize: '0.6rem' }} />
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-all' }}>
                                  {sale.email}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </>
          ) : (
            <Alert severity="info" sx={{ backgroundColor: 'rgba(255,255,255,0.9)' }}>
              No commission data available for this period.
            </Alert>
          )}
        </Paper>
      )}

      {/* Loading state for modules */}
      {modulesLoading && (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2, mt: 1.5, color: '#666' }}>
            Loading your modules...
          </Typography>
        </Box>
      )}

      {/* Error state for modules */}
      {modulesError && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="600">
            ⚠️ Could not load modules from permissions system
          </Typography>
          <Typography variant="body2">
            Using fallback module configuration based on your role.
          </Typography>
        </Alert>
      )}

      {/* Module Categories - Two Column Layout */}
      {!modulesLoading && (
        <Grid container spacing={3}>
          {Object.entries(groupedModules).map(([category, modules]) => (
          <Grid item xs={12} md={6} key={category}>
            <Paper elevation={0} sx={{ p: 3, height: '100%', bgcolor: '#fafafa' }}>
              <Typography
                variant="h6"
                fontWeight="600"
                sx={{ mb: 2, color: '#2C3E50', display: 'flex', alignItems: 'center', gap: 1 }}
              >
                {category}
                <Chip
                  label={modules.length}
                  size="small"
                  sx={{
                    bgcolor: '#4B88A2',
                    color: 'white',
                    fontWeight: 600,
                    height: 20,
                  }}
                />
              </Typography>

              {/* Compact List of Modules */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {modules.map((module) => (
                  <Card
                    key={module.id}
                    onClick={() => handleModuleClick(module.path)}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateX(4px)',
                        boxShadow: 2,
                        bgcolor: '#fff',
                        '& .module-icon': {
                          color: module.color,
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          className="module-icon"
                          sx={{
                            color: module.color,
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {module.icon}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight="600"
                            sx={{ color: '#2C3E50', lineHeight: 1.3 }}
                          >
                            {module.title}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {module.description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Paper>
          </Grid>
        ))}
        </Grid>
      )}

      {/* No modules message */}
      {availableModules.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No modules available for your current permissions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please contact your administrator for access
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default ModularDashboard;
